import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import { ChevronDown, Columns3, Info } from "lucide-react";
import { ChangeEvent, ReactNode, useCallback, useState } from "react";
import { useSearchParams } from "react-router";

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { PaginationInfo } from "@/data/types";

import { ResponsiveButton } from "./responsive-button";
import { TablePagination } from "./table-pagination";
import { TableSizeDropdown } from "./table-size-dropdown";
import { TableSort } from "./table-sort";

type Props<TData, TValue> = {
  columns: ColumnDef<TData, TValue>[];
  data?: TData[];
  isLoading?: boolean;
  extraButtons?: ReactNode;
  defaultColumnVisibility?: VisibilityState;
  paginationInfo?: PaginationInfo;
  filterPlaceholder?: string;
  filterButtons?: ReactNode;
  filterInputColumn?: string;
  sortableColumns?: string[];
  defaultSort?: {
    column: string;
    direction: "asc" | "desc";
  };
};

export const ServerDataTable = <TData, TValue>({
  columns,
  data,
  isLoading,
  extraButtons,
  defaultColumnVisibility = {},
  paginationInfo,
  filterPlaceholder,
  filterButtons,
  filterInputColumn = "search",
  sortableColumns,
  defaultSort,
}: Props<TData, TValue>) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    defaultColumnVisibility,
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterValue, setFilterValue] = useState(
    searchParams.get(filterInputColumn) || "",
  );

  const table = useReactTable({
    data: data ?? [],
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    manualPagination: true,
    pageCount: paginationInfo?.pages,
    state: { sorting, columnFilters, columnVisibility },
  });

  const handleSearchInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setFilterValue(value);

      const debounce = setTimeout(() => {
        const tempParams = new URLSearchParams(searchParams);

        if (value.length === 0) {
          tempParams.delete(filterInputColumn);
        } else {
          tempParams.delete("page");
          tempParams.set(filterInputColumn, value);
        }

        setSearchParams(tempParams);
      }, 300);

      return () => clearTimeout(debounce);
    },
    [filterInputColumn, searchParams, setSearchParams],
  );

  return (
    <div>
      <div className="flex justify-between items-center py-4 gap-2">
        <div className="flex gap-2 flex-1">
          <Input
            placeholder={filterPlaceholder || "Filter"}
            value={filterValue}
            onChange={handleSearchInputChange}
            className="flex-1 max-w-[350px]"
          />

          {filterButtons}
        </div>

        <div className="flex items-center gap-2">
          {extraButtons}
          <TableSort
            defaultSort={defaultSort}
            columns={table
              .getAllColumns()
              .filter((column) => {
                return (
                  column.columnDef.header &&
                  column.columnDef.id &&
                  sortableColumns?.includes(column.columnDef.id)
                );
              })
              .map((column) => ({
                id: column.id,
                label: column.columnDef.header?.toString() ?? "",
              }))}
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <ResponsiveButton
                leftIcon={<Columns3 />}
                rightIcon={<ChevronDown />}
                variant="outline"
                className="ml-auto"
              >
                Columns
              </ResponsiveButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter(
                  (column) => column.getCanHide() && !!column.columnDef.header,
                )
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.columnDef.header?.toString()}
                    </DropdownMenuCheckboxItem>
                  );
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="rounded-md border">
        <Table className={isLoading ? "opacity-45" : undefined}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      <div className="flex items-center">
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}

                        {header.column.columnDef.meta?.tooltip && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Info className="ml-2 w-4 h-4" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{header.column.columnDef.meta.tooltip}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center mt-4">
        <TableSizeDropdown paginationInfo={paginationInfo} />

        <TablePagination paginationInfo={paginationInfo} />

        <div className="flex-1"></div>
      </div>
    </div>
  );
};
